// frontend/src/components/layout/AppTopBar.js
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { FaSearch } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { debounce } from 'lodash';
import apiClient from '../../api/apiClient';
import FarzanehLogo from '../../assets/farzaneh.webp';

const AppTopBar = () => {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const navigate = useNavigate();
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSuggestions = useCallback(
    debounce(async (searchTerm) => {
      if (searchTerm.length < 2) {
        setSuggestions([]);
        setShowDropdown(false);
        return;
      }
      setLoading(true);
      try {
        // درخواست همزمان به هر دو endpoint (محصولات و کتاب‌ها)
        const [productsRes, booksRes] = await Promise.all([
          apiClient.get(`/products/suggest/?q=${encodeURIComponent(searchTerm)}`),
          apiClient.get(`/books/suggest/?q=${encodeURIComponent(searchTerm)}`),
        ]);

        // اضافه کردن فیلد type به هر آیتم برای تشخیص مسیر
        const products = (productsRes.data || []).map(item => ({ ...item, type: 'product' }));
        const books = (booksRes.data || []).map(item => ({ ...item, type: 'book' }));

        // ترکیب نتایج و مرتب‌سازی بر اساس عنوان (اختیاری)
        const combined = [...products, ...books];
        combined.sort((a, b) => a.title.localeCompare(b.title));
        
        setSuggestions(combined);
        setShowDropdown(combined.length > 0);
      } catch (error) {
        console.error('خطا در دریافت پیشنهادات جستجو:', error);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 300),
    []
  );

  const handleChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    fetchSuggestions(value);
    if (!value.trim()) setShowDropdown(false);
  };

  const handleSelect = (item) => {
    if (item.type === 'product' && item.slug) {
      navigate(`/products/${item.slug}`);
    } else if (item.type === 'book' && item.slug) {
      navigate(`/books/${item.slug}`);
    }
    setQuery('');
    setSuggestions([]);
    setShowDropdown(false);
  };

  const gradientStyle = {
    background: 'linear-gradient(180deg, rgba(0, 157, 255, 1), rgba(255, 255, 255, 0))',
  };

  return (
    <header 
      className="bg-white shadow-md py-3 px-4 rtl relative z-[999]" 
      style={gradientStyle}
    >
      <div className="container mx-auto flex items-center justify-between gap-4">
        <div className="flex-shrink-0">
          <img src={FarzanehLogo} alt="لوگوی فرزانه" className="h-10 w-auto" />
        </div>
        <div className="relative flex-1 max-w-md" ref={wrapperRef}>
          <div className="relative">
            <FaSearch className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="عبارت یا موضوع مورد نظر خود را جستجو کنید"
              className="w-full py-2 pr-10 pl-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
              value={query}
              onChange={handleChange}
              onFocus={() => query.trim().length >= 2 && suggestions.length > 0 && setShowDropdown(true)}
            />
          </div>
          {showDropdown && (
            <ul className="absolute z-[60] w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
              {loading && <li className="px-4 py-2 text-gray-500 text-sm">در حال جستجو...</li>}
              {!loading &&
                suggestions.map((item) => (
                  <li
                    key={`${item.type}-${item.id}`}
                    onClick={() => handleSelect(item)}
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm text-gray-800"
                  >
                    {item.title}
                    {item.type === 'book' && (
                      <span className="mr-2 text-xs text-gray-400">(کتاب)</span>
                    )}
                    {item.type === 'product' && (
                      <span className="mr-2 text-xs text-gray-400">(درس‌گفتار و محتوای دیجیتال)</span>
                    )}
                  </li>
                ))}
              {!loading && suggestions.length === 0 && query.length >= 2 && (
                <li className="px-4 py-2 text-gray-500 text-sm">نتیجه‌ای یافت نشد</li>
              )}
            </ul>
          )}
        </div>
      </div>
    </header>
  );
};

export default AppTopBar;